import {identity} from '@packages/utilities/fp/utils';
import {getString} from '@packages/utilities/json';
import {Plugins, TypeImplementation, Result} from '@packages/systems/wfdl';
import {call1, _$, tyfun, $, func} from '@packages/utilities/wfdl-helpers';
import * as wast from '@packages/systems/wfdl-ast';
import * as wq from '@packages/systems/wfdl-Qualified';
import {isVideoURL} from '@packages/systems/core/utils/FigureUtils';
import {VideoSmall1 as VideoIcon} from '@packages/systems/ui/icons';
import {createSharedTextPluginValues} from '@packages/systems/plugins/Text';
import {
  makeRenderFunction,
  makeOptionalAlias,
} from '@packages/systems/plugins-utils/ValidatedTextInput';

export const TVideoLink = wast.TTypeConstructor(
  wq.qualify(Plugins.name('Video'), wq.typeName('Video'))
);

export const TOptionalVideoLink = wast.TTypeConstructor(
  wq.qualify(Plugins.name('Video'), wq.typeName('Optional Video'))
);

export const plugin = Plugins.PrivilegedPlugin({
  dataTypes: {
    Video: TypeImplementation.Custom({
      parseValue: (json) => {
        const str = getString(json);
        if (str === undefined)
          return Result.Error('Expected a string for Video link.');
        return isVideoURL(str)
          ? Result.Ok(str)
          : Result.Error('Expected a valid Video link literal.');
      },
      render: makeRenderFunction('Video'),
      icon: TypeImplementation.IconByName(
        wq.qualify(wq.pluginName('Video'), wq.iconName('Video'))
      ),
    }),
    'Optional Video': makeOptionalAlias('Video'),
  },

  values: {
    ...createSharedTextPluginValues(TVideoLink, [
      'Equals',
      'Does not equal',
      'Contains',
      'Does not contain',
      'Starts with',
      'Does not start with',
      'Ends with',
      'Does not end with',
    ]),

    'Parse Video Link': {
      envType: {
        dataType: wast.functionOf(
          wast.TText,
          wast.TUnion({
            Error: wast.TText,
            Ok: TVideoLink,
          })
        ),
        exported: 'hidden',
      },
      // TODO - discuss if we want to make an Embedly request here
      // @ts-expect-error - TS7006 - Parameter 'inputText' implicitly has an 'any' type.
      value: (inputText) =>
        isVideoURL(inputText)
          ? {tag: 'Ok', val: inputText}
          : {tag: 'Error', val: 'Must be a valid video URL.'},
    },

    'To Text': {
      envType: {
        dataType: wast.functionOf(TVideoLink, wast.TText),
        exported: 'visible',
        implicit: 'prefilledArgs',
      },
      value: identity,
    },

    'Option VideoLink to Text': {
      envType: {
        dataType: tyfun([wast.TText, TOptionalVideoLink], wast.TText),
        implicit: 'prefilledArgs',
        exported: 'visible',
      },
      expression: func(
        [wq.identifier('fallback text'), wq.identifier('optional video link')],
        wast.ECase({
          value: $('optional video link'),
          cases: {
            Some: _$('Video', 'To Text'),
            None: func([wq.identifier('_')], $('fallback text')),
          },
        })
      ),
    },
  },

  icons: {
    Video: VideoIcon,
  },
});

export const parseVideoLinkRef = _$('Video', 'Parse Video Link');
export const parseVideoLink = call1(parseVideoLinkRef);
